import { getSupabase } from "@/lib/supabase-lazy";
import { SITE_URL } from "@/lib/site-url";
import { isCorporateArticle } from "@/lib/insightsAudience";

const OG_IMAGE = `${SITE_URL}/og-retail-lemon.jpg`;

export interface ArticleSsrMeta {
  title: string;
  description: string;
  image: string;
  pathEs: string;
  pathEn: string;
  lang: "es" | "en";
  /**
   * Fila completa del artículo, para que el HTML servido lleve H1 y cuerpo
   * reales. Sin esto Google veía un esqueleto de carga y lo trataba como
   * soft 404.
   */
  article?: Record<string, unknown> | undefined;
  category?: Record<string, unknown> | null | undefined;
}

const ARTICLE_COLUMNS =
  "id, slug, slug_es, slug_en, article_type, title_es, title_en, excerpt_es, excerpt_en, content_html_es, content_html_en, cover_image_url, cover_image_alt, featured_image_alt_es, featured_image_alt_en, featured_image_title, keywords, seo_title_es, seo_title_en, seo_description_es, seo_description_en, published_at, updated_at, category_id";

/**
 * Metadatos SSR de un artículo. El componente <SEO /> del artículo sólo se
 * pinta cuando los datos ya están en cliente, así que el HTML servido necesita
 * estas etiquetas desde el `head()` de la ruta.
 */
export async function fetchArticleSsrMeta(
  slug: string,
  lang: "es" | "en",
  basePathEs: string,
  basePathEn: string,
): Promise<ArticleSsrMeta | null> {
  if (!/^[a-z0-9][a-z0-9-]{0,200}$/i.test(slug)) return null;
  try {
    const supabase = await getSupabase();
    let { data } = await supabase
      .from("blog_articles")
      .select(ARTICLE_COLUMNS)
      .eq("status", "published")
      .or(`slug.eq.${slug},slug_es.eq.${slug},slug_en.eq.${slug}`)
      .maybeSingle();
    if (!data) {
      // Artículo Club Pro: el cuerpo completo está reservado, pero la página debe
      // existir con su URL propia y el arranque real del texto.
      const { data: pub } = await supabase
        .from("blog_articles_public")
        .select(
          "id, slug, slug_es, slug_en, article_type, title_es, title_en, excerpt_es, excerpt_en, cover_image_url, cover_image_alt, featured_image_alt_es, featured_image_alt_en, featured_image_title, keywords, seo_title_es, seo_title_en, seo_description_es, seo_description_en, published_at, updated_at, category_id",
        )
        .or(`slug.eq.${slug},slug_es.eq.${slug},slug_en.eq.${slug}`)
        .maybeSingle();
      if (!pub) return null;
      const { data: teaser } = await supabase.rpc("get_article_teaser", {
        _slug: slug,
        _lang: lang,
      });
      const teaserHtml = Array.isArray(teaser) ? ((teaser[0] as any)?.teaser_html ?? "") : "";
      data = {
        ...(pub as any),
        content_html_es: lang === "es" ? teaserHtml : "",
        content_html_en: lang === "en" ? teaserHtml : "",
      } as any;
    }
    const a = data as Record<string, string | null>;
    const rawTitle =
      (lang === "es" ? a["seo_title_es"] || a["title_es"] : a["seo_title_en"] || a["title_en"]) || "";
    const description =
      (lang === "es"
        ? a["seo_description_es"] || a["excerpt_es"]
        : a["seo_description_en"] || a["excerpt_en"]) || "";
    let category: Record<string, unknown> | null = null;
    if (a["category_id"]) {
      const { data: cat } = await supabase
        .from("blog_categories")
        .select("id, slug, name_es, name_en, color")
        .eq("id", a["category_id"])
        .maybeSingle();
      category = (cat as Record<string, unknown> | null) ?? null;
    }
    return {
      title: rawTitle ? `${rawTitle} — Retail Lemon` : "Retail Lemon",
      description,
      image: a["cover_image_url"] || OG_IMAGE,
      pathEs: `${basePathEs}/${a["slug_es"] || a["slug"] || slug}`,
      pathEn: `${basePathEn}/${a["slug_en"] || a["slug"] || slug}`,
      lang,
      article: data as unknown as Record<string, unknown>,
      category,
    };
  } catch {
    return null;
  }
}


/** Convierte los metadatos en el objeto que espera `head()`. */
export function articleHead(meta: ArticleSsrMeta | null) {
  if (!meta) return { meta: [] };
  const url = `${SITE_URL}${meta.lang === "es" ? meta.pathEs : meta.pathEn}`;
  const a = (meta.article ?? {}) as Record<string, unknown>;
  // Los artículos corporativos (M&A, lujo B2B, due diligence, hubs, EBITDA de
  // gran cuenta) no compiten en buscadores: siguen accesibles pero noindex.
  const corporate =
    a["article_type"] !== "premium" &&
    isCorporateArticle({
      titleEs: (a["title_es"] as string) ?? null,
      titleEn: (a["title_en"] as string) ?? null,
      keywords: (a["keywords"] as string) ?? null,
      categorySlug: (meta.category?.["slug"] as string) ?? null,
    });
  return {
    meta: [
      { title: meta.title },
      { name: "description", content: meta.description },
      {
        name: "robots",
        content: corporate
          ? "noindex, follow"
          : "index, follow, max-image-preview:large, max-snippet:-1",
      },
      { property: "og:title", content: meta.title },
      { property: "og:description", content: meta.description },
      { property: "og:url", content: url },
      { property: "og:type", content: "article" },
      { property: "og:image", content: meta.image },
      { property: "og:locale", content: meta.lang === "es" ? "es_ES" : "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: meta.title },
      { name: "twitter:description", content: meta.description },
      { name: "twitter:image", content: meta.image },
    ],
    // Mismo href que emite <SEO /> en cliente, para que el HTML servido ya
    // lleve canonical sin generar una URL distinta al hidratar.
    links: [{ rel: "canonical", href: url }],
  };

}
